import { useFormikContext } from 'formik'
import React, { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import {
  getFundingCurrencySelectField,
  isVisibleFundingFormField,
} from '@pages/Traders-Room/shared/hooks/useFundingCurrencySelector'

import { useSessionLanguage } from '@context/SessionSettingsContext'

import { DocumentFileUpload } from '@ui/components/FileUpload/DocumentFileUpload'
import { FileData } from '@ui/components/FileUpload/types'
import { PopoverInput, PopoverOption } from '@ui/components/Popover/PopoverInput/PopoverInput'
import { PlusIcon } from '@ui/icons/Plus.icon'

import {
  useCategorySizesReadContext,
  useCategorySizesWriteContext,
} from '@utils/CategorySizesContext'
import { validateRequired } from '@utils/formValidation'

import { DocumentCategoryType } from '@model/DocumentCategories'
import { isPaymentProviderBankType } from '@model/PaymentProviderType'

import { createFormField } from '@global/formField/FormField'
import { isFormFieldKnownType } from '@global/formField/formFieldsFactory/FormFieldComponentType'
import { FormFieldsFactory } from '@global/formField/formFieldsFactory/FormFieldsFactory'

import { Box } from '@shared/components/Box'

import { buildWithdrawalBankAccountFields } from '../WithdrawalFlowLayout/helpers'
import type { WithdrawalFormValues } from '../WithdrawalFlowLayout/types'
import { useWithdrawalBankAccounts } from '../hooks/useWithdrawalBankAccounts'

const ADD_NEW_BANK_ACCOUNT_OPTION_ID = '__add_new__'

const FormField = createFormField<WithdrawalFormValues>()

const NAME_OF_ACCOUNT_SNAKE = 'name_of_account'

export const WithdrawalFormFields: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const locale = useSessionLanguage()
  const { values, setFieldValue, isSubmitting } = useFormikContext<WithdrawalFormValues>()

  const provider = values.paymentProvider
  const isBankProvider = isPaymentProviderBankType(provider?.providerCategory?.id)

  const { bankAccounts, isBankAccountsLoading } = useWithdrawalBankAccounts(isBankProvider)

  const rawFields = useMemo(
    () => provider?.parameters?.fields ?? [],
    [provider?.parameters?.fields]
  )

  const fundingCurrencyFieldName = useMemo(
    () => getFundingCurrencySelectField(rawFields)?.name,
    [rawFields]
  )

  const fields = useMemo(
    () =>
      rawFields
        .filter((field) => isVisibleFundingFormField(field, fundingCurrencyFieldName))
        .filter(isFormFieldKnownType)
        .map((field) => {
          const isNameOfAccount =
            field.name.endsWith(NAME_OF_ACCOUNT_SNAKE) || field.name.endsWith('nameOfAccount')

          return isNameOfAccount ? { ...field, label: t('Cardholder Name') } : field
        }),
    [rawFields, fundingCurrencyFieldName, t]
  )

  const bankAccountOptions = useMemo<PopoverOption<string>[]>(
    () => [
      {
        id: ADD_NEW_BANK_ACCOUNT_OPTION_ID,
        name: t('Wallet.Add New Bank Account'),
        startIcon: PlusIcon,
      },
      ...bankAccounts.map((account) => ({
        id: account.id,
        name: account.accountName,
        description: account.bankAccount,
        selectionVariant: 'radio' as const,
      })),
    ],
    [bankAccounts, t]
  )

  const handleAddNewBankAccount = () => {
    navigate('/profile/bank-accounts/new', {
      replace: true,
      state: { withdrawalReturnPath: location.pathname, locale },
    })
  }

  const handleBankAccountSelect = (option: PopoverOption<string>) => {
    if (option.id === ADD_NEW_BANK_ACCOUNT_OPTION_ID) {
      handleAddNewBankAccount()
      return
    }

    const selected = bankAccounts.find((account) => account.id === option.id)
    if (!selected) return

    setFieldValue('bankAccount', selected)
    setFieldValue('fields', { ...values.fields, ...buildWithdrawalBankAccountFields(selected) })
  }

  const createdBankAccountId = (location.state as { createdBankAccountId?: string } | null)
    ?.createdBankAccountId

  const consumedCreatedIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!createdBankAccountId || !isBankProvider) return
    if (consumedCreatedIdRef.current === createdBankAccountId) return
    const created = bankAccounts.find((account) => account.id === createdBankAccountId)
    if (!created) return

    consumedCreatedIdRef.current = createdBankAccountId
    setFieldValue('bankAccount', created)
    setFieldValue('fields', { ...values.fields, ...buildWithdrawalBankAccountFields(created) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankAccounts, createdBankAccountId, isBankProvider])

  const hasBankAccounts = bankAccounts.length > 0

  useEffect(() => {
    if (isBankAccountsLoading) return
    setFieldValue('hasBankAccounts', hasBankAccounts)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBankAccounts, isBankAccountsLoading])

  const isBankAccountSelected = isBankProvider && Boolean(values.bankAccount)
  const showBankAccountSelect = isBankProvider && !isBankAccountsLoading && hasBankAccounts
  const showBankAccountDetails =
    !isBankProvider ||
    isBankAccountSelected ||
    isSubmitting ||
    (!isBankAccountsLoading && !hasBankAccounts)
  const showBankStatementUpload = isBankProvider && showBankAccountDetails && !isBankAccountSelected

  return (
    <Box flex direction='column' gap='x16' fullWidth>
      {showBankAccountSelect && (
        <PopoverInput
          showLabel
          name='bankAccount'
          label={t('Wallet.To My Bank Account')}
          placeholder={t('Wallet.Choose Bank Account')}
          value={values.bankAccount?.id}
          options={bankAccountOptions}
          hasCheckMark={false}
          required
          onChange={handleBankAccountSelect}
        />
      )}
      {isBankProvider && showBankAccountDetails && (
        <FormField
          name='fields.accountName'
          label={t('Beneficiary Name')}
          validate={validateRequired(t('Validation.Required'))}
          required
          maxlength={128}
          disabled={isBankAccountSelected}
        />
      )}
      {showBankAccountDetails && fields.length > 0 && (
        <FormFieldsFactory fields={fields} disabled={isBankAccountSelected} />
      )}
      {showBankStatementUpload && <BankStatementUpload />}
      {showBankAccountDetails && (
        <FormField name='comment' label={t('Wallet.Additional Comment')} />
      )}
    </Box>
  )
}

const BankStatementUpload: React.FC = () => {
  const { t } = useTranslation()
  const { values, setFieldValue } = useFormikContext<WithdrawalFormValues>()
  const { categorySizes } = useCategorySizesReadContext()
  const { refreshCategorySizes } = useCategorySizesWriteContext()

  useEffect(() => {
    refreshCategorySizes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleUpload = (data: FileData) => {
    setFieldValue('bankDepositProof', data)
  }

  const handleRemove = () => {
    setFieldValue('bankDepositProof', undefined)
  }

  return (
    <DocumentFileUpload
      title={t('Wallet.Bank Deposit Proof / Bank Statement')}
      documentCategory={DocumentCategoryType.Internal}
      categorySizes={categorySizes}
      file={values.bankDepositProof as FileData}
      onUpload={handleUpload}
      onRemove={handleRemove}
    />
  )
}
